import { cn } from "@/utils";

export interface ChipToggleOption<V extends string> {
  value: V;
  label: string;
  /** Optional dot color for visual differentiation (semantic data-type marker). */
  dotClassName?: string;
}

export interface ChipToggleProps<V extends string> {
  value: V;
  onChange: (next: V) => void;
  options: ChipToggleOption<V>[];
  /** Optional inline label rendered before the chips. */
  label?: string;
  className?: string;
}

/**
 * ChipToggle — segmented chip row for filter values that benefit from being
 * tường minh (visible at a glance). Better than a Select when the option
 * count is small (≤ 6) and the user wants to compare at-a-glance instead of
 * opening a menu. Active chip uses --primary; inactive shows the optional
 * data-type dot for semantic differentiation.
 */
export function ChipToggle<V extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: ChipToggleProps<V>) {
  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      {label && (
        <span className="text-xs font-medium text-[var(--muted-foreground)] shrink-0 hidden sm:inline">
          {label}
        </span>
      )}
      <div className="overflow-x-auto">
        <div className="inline-flex rounded-lg border border-[var(--border)] bg-[var(--background)] p-0.5 w-max">
          {options.map((opt) => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={cn(
                  "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
                  active
                    ? "bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm"
                    : "text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--accent)]"
                )}
              >
                {opt.dotClassName && !active && (
                  <span className={cn("size-1.5 rounded-full", opt.dotClassName)} />
                )}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
